import { useCallback, useEffect, useRef, useState } from "react";

const NOTES_DEBOUNCE_MS = 500;

interface IUseExerciseNotesArgs {
  planExerciseId: number;
  initialNotes: string | null;
  disabled: boolean;
  onUpdate: (planExerciseId: number, notes: string) => Promise<void> | void;
}

export const useExerciseNotes = ({
  planExerciseId,
  initialNotes,
  disabled,
  onUpdate,
}: IUseExerciseNotesArgs) => {
  const [value, setValue] = useState<string>(initialNotes ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFlushRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setValue(initialNotes ?? "");
  }, [planExerciseId, initialNotes]);

  const handleChange = useCallback(
    (next: string) => {
      setValue(next);
      if (disabled) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const flush = () => {
        debounceRef.current = null;
        pendingFlushRef.current = null;
        if ((initialNotes ?? "") === next) return;
        void onUpdate(planExerciseId, next);
      };
      pendingFlushRef.current = flush;
      debounceRef.current = setTimeout(flush, NOTES_DEBOUNCE_MS);
    },
    [disabled, initialNotes, onUpdate, planExerciseId],
  );

  // On unmount, flush any pending change instead of dropping it — so closing
  // the dialog mid-debounce still saves the user's last edit.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      pendingFlushRef.current?.();
    };
  }, []);

  return { value, handleChange };
};
